import _slicedToArray from "/opt/build/repo/web/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { __rest } from "tslib";
import * as React from 'react';
import { Form } from '../..';
import { useDebounce } from '../../hooks/useDebounce';
import { useThrottle } from '../../hooks/useThrottle';
import { ClassNames } from '../../utils/classNames';
import { InputWrapper } from '../inputWrapper/inputWrapper.component';
var DebounceInputBase = React.forwardRef(function (_a, ref) {
  var milliseconds = _a.milliseconds,
      value = _a.value,
      onValueChange = _a.onValueChange,
      onChange = _a.onChange,
      nativeProps = __rest(_a, ["milliseconds", "value", "onValueChange", "onChange"]);

  var _useDebounce = useDebounce(milliseconds, value, onValueChange),
      _useDebounce2 = _slicedToArray(_useDebounce, 2),
      actualValue = _useDebounce2[0],
      setActualValue = _useDebounce2[1];

  var onChangeEvent = React.useCallback(function (e) {
    setActualValue(e.currentTarget.value);
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  }, [setActualValue, onChange]);
  return React.createElement("input", Object.assign({
    ref: ref,
    value: actualValue,
    onChange: onChangeEvent
  }, nativeProps));
});
var ThrottledInputBase = React.forwardRef(function (_a, ref) {
  var milliseconds = _a.milliseconds,
      value = _a.value,
      onValueChange = _a.onValueChange,
      onChange = _a.onChange,
      nativeProps = __rest(_a, ["milliseconds", "value", "onValueChange", "onChange"]);

  var _useThrottle = useThrottle(milliseconds, value, onValueChange),
      _useThrottle2 = _slicedToArray(_useThrottle, 2),
      actualValue = _useThrottle2[0],
      setActualValue = _useThrottle2[1];

  var onChangeEvent = React.useCallback(function (e) {
    setActualValue(e.currentTarget.value);
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  }, [setActualValue, onChange]);
  return React.createElement("input", Object.assign({
    ref: ref,
    value: actualValue,
    onChange: onChangeEvent
  }, nativeProps));
});
/** A component which wraps up a native input element with some binding logic and some repeated elements (icons and stuff) for components which only contain a single input element. */

export var Input = React.forwardRef(function (_a, ref) {
  var bind = _a.bind,
      onChange = _a.onChange,
      value = _a.value,
      className = _a.className,
      leftIcon = _a.leftIcon,
      rightIcon = _a.rightIcon,
      leftOverlay = _a.leftOverlay,
      rightOverlay = _a.rightOverlay,
      validationErrorMessages = _a.validationErrorMessages,
      validationMode = _a.validationMode,
      validationErrorIcon = _a.errorIcon,
      pending = _a.pending,
      above = _a.above,
      below = _a.below,
      disabled = _a.disabled,
      disableOnPending = _a.disableOnPending,
      statusPosition = _a.statusPosition,
      hideIconOnStatus = _a.hideIconOnStatus,
      onValueChange = _a.onValueChange,
      scrollValidationErrorsIntoView = _a.scrollValidationErrorsIntoView,
      delay = _a.delay,
      nativeProps = __rest(_a, ["bind", "onChange", "value", "className", "leftIcon", "rightIcon", "leftOverlay", "rightOverlay", "validationErrorMessages", "validationMode", "errorIcon", "pending", "above", "below", "disabled", "disableOnPending", "statusPosition", "hideIconOnStatus", "onValueChange", "scrollValidationErrorsIntoView", "delay"]);

  var internalRef = React.useRef(null);
  React.useImperativeHandle(ref, function () {
    return internalRef.current;
  }, [internalRef]);

  var _Form$useBindingState = Form.useBindingState(bind, {
    value: value === null || value === void 0 ? void 0 : value.toString(),
    validationErrorMessages: validationErrorMessages,
    validationMode: validationMode,
    validationErrorIcon: validationErrorIcon
  }),
      _Form$useBindingState2 = _slicedToArray(_Form$useBindingState, 3),
      boundValue = _Form$useBindingState2[0],
      setBoundValue = _Form$useBindingState2[1],
      bindConfig = _Form$useBindingState2[2];

  var onBindValueChange = React.useCallback(function (currentValue) {
    var _a, _b, _c;

    if (bind) {
      var formattedValue = ((_c = (_b = (_a = bind.bindConfig) === null || _a === void 0 ? void 0 : _a.format) === null || _b === void 0 ? void 0 : _b.toData) === null || _c === void 0 ? void 0 : _c.call(_b, currentValue)) || currentValue;
      bind.setValue(formattedValue);
    }
  }, [bind]);
  var onChangeEvent = React.useCallback(function (event) {
    onChange === null || onChange === void 0 ? void 0 : onChange(event);
    var currentValue = event.currentTarget.value;
    setBoundValue === null || setBoundValue === void 0 ? void 0 : setBoundValue(currentValue);
    onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(currentValue);
  }, [setBoundValue, onBindValueChange, onChange]);
  /** onChange used for throttled inputs */

  var onValueChangeEvent = React.useCallback(function (currentValue) {
    setBoundValue === null || setBoundValue === void 0 ? void 0 : setBoundValue(currentValue);
    onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(currentValue);
  }, [onValueChange, onBindValueChange, setBoundValue]);
  var inputProps = {
    className: 'arm-input-base-input',

    /** fallback to an empty string if bind is passed in but bound value is undefined to avoid React warning */
    value: boundValue !== null && boundValue !== void 0 ? boundValue : bind && '',
    disabled: disabled
  };
  return React.createElement(InputWrapper, {
    className: ClassNames.concat(className, 'arm-input-base'),
    leftIcon: leftIcon,
    rightIcon: rightIcon,
    leftOverlay: leftOverlay,
    rightOverlay: rightOverlay,
    validationErrorMessages: bindConfig.validationErrorMessages,
    errorIcon: bindConfig.validationErrorIcon,
    validationMode: bindConfig.validationMode,
    pending: pending,
    disabled: disabled,
    above: above,
    statusPosition: statusPosition,
    scrollValidationErrorsIntoView: scrollValidationErrorsIntoView,
    below: below,
    disableOnPending: disableOnPending,
    hideIconOnStatus: hideIconOnStatus,
    onClick: function onClick() {
      var _a;

      return (_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  }, (delay === null || delay === void 0 ? void 0 : delay.mode) === 'debounce' && !!delay.milliseconds && React.createElement(DebounceInputBase, Object.assign({}, nativeProps, {
    milliseconds: delay.milliseconds
  }, inputProps, {
    onChange: onChange,
    onValueChange: onValueChangeEvent,
    ref: internalRef
  })), (delay === null || delay === void 0 ? void 0 : delay.mode) === 'throttle' && !!delay.milliseconds && React.createElement(ThrottledInputBase, Object.assign({}, nativeProps, {
    milliseconds: delay.milliseconds
  }, inputProps, {
    onChange: onChange,
    onValueChange: onValueChangeEvent,
    ref: internalRef
  })), !(delay === null || delay === void 0 ? void 0 : delay.milliseconds) && React.createElement("input", Object.assign({}, nativeProps, inputProps, {
    onChange: onChangeEvent,
    ref: internalRef
  })));
});